import getEventValue from '../../utils/eventValue.js';
import { axios } from '@boot/axios.js';
import { i18n } from '@boot/i18n.js';
import { notifyError } from '@boot/popups.js';

export const MAX_GRAPH_PERFORMANCE_VALUE = 2;

export default class Graph {
    constructor({ facilityId, assetId }) {
        this.init(facilityId, assetId);
    }

    /**
     * Initialize some of the fields required
     * for the class
     * @param {boolean} enable A flag which
     * determines whether the graph is enabled
     * or not
     */
    init(facilityId = null, assetId = null) {
        this.performanceChart = {
            performance: [],
            events: [],
            lastUpdated: null,
            isDowntimeEventInProgress: false
        };
        this.GRAPH_TIME_RANGE_HOURS = 12;
        // max allowed distance between two consecutive data points
        // if the distance is larger, we fill the gap
        this.GRAPH_GAP_TIME_RANGE_MINUTES = 2;
        // frequency at which data points are added to the graph
        // when filling the gap
        this.GRAPH_REFRESH_TIME_RANGE_MINUTES = 1;
        this.loading = false;
        this.facilityId = facilityId;
        this.assetId = assetId;
    }

    /**
     * Init event data received during real time on the asset
     * dashboard.
     * @param {object} event object received from the SignalR
     */
    getEventData(event) {
        return {
            timestamp: getEventValue({
                event: event,
                key: 'timestamp'
            }),
            receivedAt: getEventValue({
                event: event,
                key: 'receivedAt'
            }),
            date: getEventValue({ event: event, key: 'date' }),
            currentDowntime: parseInt(
                getEventValue({
                    event: event,
                    key: 'currDowntimeInSec',
                    defaultValue: 0
                })
            ),
            performancePoint: getEventValue({
                event: event,
                key: 'performance1Min',
                defaultValue: 0
            })
        };
    }

    /**
     * check if the event is starting or finishing the downtime and populate
     * the downtime fields or attributes accordingly.
     * @returns None
     */
    updateDowntimes(dataPoint) {
        const { date, currentDowntime } = dataPoint;
        // if the events is not defined or not an array return
        if (!Array.isArray(this.performanceChart['events'])) {
            return;
        }
        // if current downtime seconds are greater than 0 seconds
        if (currentDowntime > 0) {
            // if downtime event is not recorded yet, add to the events list
            if (!this.performanceChart['isDowntimeEventInProgress']) {
                const currentDowntimeEventsLength =
                    this.performanceChart['events'].length;
                if (
                    currentDowntimeEventsLength == 0 ||
                    this.performanceChart['events'][
                        currentDowntimeEventsLength - 1
                    ]['end'] < date
                ) {
                    // enter the downtime event
                    this.performanceChart['events'].push({
                        start: date,
                        end: null,
                        phase: 'in_downtime'
                    });
                    this.performanceChart['isDowntimeEventInProgress'] = true;
                }
            }
        } else if (this.performanceChart['isDowntimeEventInProgress']) {
            // if we receive the current downtime 0
            // we will have to end the downtime by setting the
            // end time of downtime event to respective event date
            // and also toggle the downtime in progress indicator
            this.performanceChart['events'][
                this.performanceChart['events'].length - 1
            ].end = date;

            this.performanceChart['isDowntimeEventInProgress'] = false;
        }
    }

    /**
     * Toggle the loading state of the graph.
     * @param {boolean} state
     */
    setLoading(state) {
        if (typeof state === 'boolean') {
            this.loading = state;
        }
    }

    /**
     * Check whether the graph is loading or not.
     * @returns  {boolean}  state of the graph
     */
    isLoading() {
        return this.loading;
    }

    /**
     * Get array of performance points with 1 min
     * intervals.
     * @returns {Array} Array of performance points
     */
    getPerformanceData() {
        return this.performanceChart['performance'];
    }

    /**
     * Get start and end date objects with the
     * difference of Graph time range hours (defaults to 12h)
     * between them
     * @returns {Array} Array of start and end date objects.
     */
    getStartEndTimes() {
        let start = new Date(
            Date.now() - this.GRAPH_TIME_RANGE_HOURS * 60 * 60 * 1000
        );
        let end = new Date();
        const startWithZeroSeconds = new Date(start.setSeconds(0, 0));
        const endWithZeroSeconds = new Date(end.setSeconds(0, 0));
        return [startWithZeroSeconds, endWithZeroSeconds];
    }

    /**
     * Populate the performance chart object field with downtime events
     * and determine if there is any downtime in progress.
     * @param {object} data response received from the backend
     * api.
     */
    setDowntimeEventsData(data) {
        // check if the asset is already in the downtime events
        const dataArrayLength = data.length;
        if (dataArrayLength > 0) {
            if (data[dataArrayLength - 1].end == null) {
                this.performanceChart['isDowntimeEventInProgress'] = true;
            }
        }
        this.performanceChart['events'] = data;
    }
    /**
     * Retrieve the downtime events.
     * @returns {Array} Array consists of all the downtime events received from
     * the backend api.
     */
    getDowntimeEventsData() {
        return this.performanceChart['events'];
    }
    /**
     * Format a performance value as percent
     * @param {*} value
     * @returns {String}
     */

    formatPerformanceValue(value) {
        const performanceValue = value || 0; // handle null values in performance points
        return Number((performanceValue * 100).toFixed(2));
    }

    /**
     * Add one performance point to the graph.
     * @param {timestamp} timestamp date object for one performance point.
     * @param {int} performance  performance percentage for that timestamp.
     */
    addPerformancePoint(timestamp, actualPerformance) {
        const perfArray = this.performanceChart.performance;

        if (perfArray.length > 0) {
            const lastTimestamp = perfArray[perfArray.length - 1][0];
            const difference = timestamp.getTime() - lastTimestamp.getTime();
            if (difference > this.GRAPH_GAP_TIME_RANGE_MINUTES * 60 * 1000) {
                let currentTimestamp = lastTimestamp;
                while (currentTimestamp <= timestamp) {
                    perfArray.push([currentTimestamp, 0, 0]);
                    currentTimestamp = new Date(
                        currentTimestamp.getTime() +
                            this.GRAPH_REFRESH_TIME_RANGE_MINUTES * 60 * 1000
                    );
                }
            }
        }
        /**
         * Include two performance values in the array to show
         * the actual performance value on the tooltip.
         * The first value is the displayed performance value it
         * is capped at MAX_GRAPH_PERFORMANCE_VALUE. This will increase
         * the graph readability in case of spikes in performance.
         */
        const displayedPerformance =
            actualPerformance > MAX_GRAPH_PERFORMANCE_VALUE
                ? MAX_GRAPH_PERFORMANCE_VALUE
                : actualPerformance;
        perfArray.push([
            timestamp,
            // The displayed performance value is used to generate the graph
            this.formatPerformanceValue(displayedPerformance),
            // Append the actual performance value to the array
            // to show the actual performance value on the tooltip.
            this.formatPerformanceValue(actualPerformance)
        ]);
    }

    /**
     * initialize all the performance timestamps while filling the gaps if present.
     * @param {object} data with performance timestamps and values received from backend api.
     * @param {object} start date object which represents the start time, the moment the asset dashboard was loaded.
     * @param {*} end  date object which represents the end time with the gap of 12 hours from the start time.
     */
    initPerformancePoints(data, start, end) {
        const timestamps = data.timestamps;
        const values = data.values;
        const maxEndDate = new Date(
            end.getTime() - this.GRAPH_GAP_TIME_RANGE_MINUTES * 60 * 1000
        );
        if (timestamps.length === 0) {
            // fill the gap from the start till
            // the end with performance value
            // equal to zero.
            this.addPerformancePoint(start, 0);
            this.addPerformancePoint(maxEndDate, 0);
        } else {
            const firstTimestamp = new Date(timestamps[0]);
            if (firstTimestamp > start) {
                this.addPerformancePoint(start, 0);
            }
            for (const [index, timestamp] of timestamps.entries()) {
                this.addPerformancePoint(new Date(timestamp), values[index]);
            }
            const lastDateMinutes = new Date(
                timestamps[timestamps.length - 1]
            ).getTime();
            const endDateMinutes = end.getTime();
            if (
                endDateMinutes - lastDateMinutes >
                this.GRAPH_GAP_TIME_RANGE_MINUTES * 60 * 1000
            ) {
                // fill the gap as well as add the last point with end timestamp and performance
                // equals to zero.
                this.addPerformancePoint(maxEndDate, 0);
            }
        }
    }

    /**
     * Get the promise for performance api call.
     * @param {start} start date string for the start date.
     * @param {end} end  date string for the end date with 12 hour gap.
     * @returns {Promise}
     */
    loadPerformanceData(start, end) {
        const metricsUrlParams = {
            asset_id: this.assetId,
            start: start.toISOString(),
            end: end.toISOString(),
            kpi: 'performance'
        };
        return new Promise((resolve, reject) => {
            axios
                .get('metrics/', { params: metricsUrlParams })
                .then(res => {
                    const data = res.data;
                    const response = {
                        timestamps: data.timestamps || [],
                        values: data.values || []
                    };
                    resolve(response);
                })
                .catch(error => {
                    console.error(
                        'Failed to load performance graph data due to ',
                        error
                    );
                    reject(error);
                });
        });
    }

    /**
     * Get the promise for downtime api call.
     * @param {start} start date string for the start date.
     * @param {end} end  date string for the end date with 12 hour gap.
     * @returns {Promise}
     */
    loadDowntimeEvents(start, end) {
        const downtimeUrlParams = {
            facility: this.facilityId,
            ordering: 'start',
            page: 1,
            start__lte: end.toISOString(),
            end__gte: start.toISOString(),
            asset__in: this.assetId,
            large_pagination: true
        };
        return new Promise((resolve, reject) => {
            axios
                .get('events/', { params: downtimeUrlParams })
                .then(res => {
                    const data = res.data;
                    const response = data.results || [];
                    resolve(response);
                })
                .catch(error => {
                    console.error(
                        'Failed to load downtime events due to ',
                        error
                    );
                    reject(error);
                });
        });
    }

    /**
     * Load the performance and downtime events from api call.
     */
    async loadData() {
        this.setLoading(true);
        const [start, end] = this.getStartEndTimes();
        try {
            const [performanceData, downtimeEventsData] = await Promise.all([
                this.loadPerformanceData(start, end),
                this.loadDowntimeEvents(start, end)
            ]);
            this.initPerformancePoints(performanceData, start, end);
            this.setDowntimeEventsData(downtimeEventsData);
        } catch (error) {
            console.error('Failed to load performance graph.', error);
            notifyError(
                i18n.global.t(
                    'Failed to load performance graph. Please reload the page.'
                )
            );
        }
        this.setLoading(false);
    }

    /**
     * Process the live events coming from sensor
     * and determine the performance as well as
     * downtime event, if any.
     * @param {object} newEvent received from SignalR
     */
    pushLiveEventData(newEvent) {
        // debugger;
        const eventData = this.getEventData(newEvent);
        const eventDate = new Date(eventData.date);
        const eventPerformance = eventData.performancePoint;
        if (this.performanceChart.performance.length > 0) {
            const lastUpdatedPerformanceDate =
                this.performanceChart.performance[
                    this.performanceChart.performance.length - 1
                ][0];
            //  check if the event difference is more than the threshold limit
            const minuteDifference =
                eventDate.getTime() - lastUpdatedPerformanceDate.getTime();
            if (
                minuteDifference >=
                this.GRAPH_REFRESH_TIME_RANGE_MINUTES * 60 * 1000
            ) {
                console.debug('Adding new data point to the graph');
                this.addPerformancePoint(eventDate, eventPerformance);
                this.updateDowntimes(eventData);
                this.performanceChart.performance.shift();
            }
        } else {
            this.addPerformancePoint(eventDate, eventPerformance);
            this.updateDowntimes(eventData);
        }
    }

    /**
     * Reset the graph object to its initial state.
     */
    reset() {
        this.init();
    }
}
